import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { type PortalShellModuleAccess, type PortalShellService } from "@/lib/portalShell";
import { toast } from "sonner";
import { Layers3, Loader2, Settings, ShieldCheck, ShieldX } from "lucide-react";
import { useLocation } from "wouter";

function StatusRow({ label, active }: { label: string; active: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      <span
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
      >
        {active ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldX className="h-3.5 w-3.5" />}
        {active ? "Sim" : "Não"}
      </span>
    </div>
  );
}

const MANAGE_CATALOG_ROLES = ["admin", "salvia_operation_admin", "channel_admin", "channel_admin_multi", "channel_operator"];

export default function Services() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();

  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(user?.channelId ?? null);
  const [draftServiceIds, setDraftServiceIds] = useState<number[]>([]);

  const platformNucleus = (user as any)?.platformNucleus;
  const services = (platformNucleus?.services || []) as PortalShellService[];
  const modules = (platformNucleus?.modules || []) as PortalShellModuleAccess[];

  const canManageCatalog = MANAGE_CATALOG_ROLES.includes(user?.role || "");
  const canChooseAnyChannel = ["admin", "salvia_operation_admin"].includes(user?.role || "");

  const { data: channelsData } = trpc.channels.list.useQuery(undefined, {
    enabled: canManageCatalog && canChooseAnyChannel,
  });

  useEffect(() => {
    if (!canManageCatalog) return;
    if (!canChooseAnyChannel) {
      setSelectedChannelId(user?.channelId ?? null);
      return;
    }
    if (!selectedChannelId && channelsData && channelsData.length > 0) {
      setSelectedChannelId(channelsData[0].id);
    }
  }, [canChooseAnyChannel, canManageCatalog, channelsData, selectedChannelId, user?.channelId]);

  const { data: catalogSummary, isLoading: isLoadingCatalog } = trpc.platformContracts.summary.useQuery(
    { channelId: selectedChannelId ?? undefined },
    { enabled: canManageCatalog && !!selectedChannelId },
  );

  useEffect(() => {
    if (!catalogSummary?.services) return;
    setDraftServiceIds(catalogSummary.services.filter((service) => service.channelEnabled).map((service) => service.id));
  }, [catalogSummary?.services, selectedChannelId]);

  const setChannelCatalogMutation = trpc.platformContracts.setChannelCatalog.useMutation({
    onSuccess: () => {
      toast.success("Catálogo comercial do canal atualizado.");
      utils.platformContracts.summary.invalidate({ channelId: selectedChannelId ?? undefined });
      utils.platformContracts.listServices.invalidate({ channelId: selectedChannelId ?? undefined });
    },
    onError: (error) => {
      toast.error(error.message || "Não foi possível atualizar o catálogo do canal.");
    },
  });

  const currentChannelLabel = useMemo(() => {
    if (!selectedChannelId) return "Canal não definido";
    const foundChannel = channelsData?.find((channel) => channel.id === selectedChannelId);
    return foundChannel?.nomeFantasia || foundChannel?.razaoSocial || `Canal #${selectedChannelId}`;
  }, [channelsData, selectedChannelId]);

  const handleToggleCatalogService = (serviceId: number, checked: boolean) => {
    setDraftServiceIds((prev) =>
      checked ? Array.from(new Set([...prev, serviceId])) : prev.filter((id) => id !== serviceId),
    );
  };

  const handleSaveCatalog = () => {
    if (!selectedChannelId) {
      toast.error("Selecione um canal para salvar o catálogo.");
      return;
    }

    setChannelCatalogMutation.mutate({
      channelId: selectedChannelId,
      serviceIds: draftServiceIds,
    });
  };

  return (
    <SalviaLayout title="Catálogo de Serviços">
      <div className="space-y-6">
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                <Layers3 className="h-3.5 w-3.5" />
                Administração da Plataforma
              </div>
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold text-slate-900">Catálogo de serviços e habilitações comerciais</h1>
                <p className="max-w-3xl text-sm leading-6 text-slate-600">
                  Esta visão organiza o catálogo administrativo da plataforma, separando o que está habilitado
                  comercialmente no canal, o que já possui contratação ativa e o que ainda depende de elegibilidade
                  operacional para virar serviço efetivo dentro do Portal PJC.
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setLocation("/modulos/nucleo")}>Voltar à Administração da Plataforma</Button>
          </div>
        </section>

        {canManageCatalog && (
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Gestão operacional</p>
                <h2 className="text-xl font-semibold text-slate-900">Catálogo comercial por canal</h2>
                <p className="max-w-3xl text-sm leading-6 text-slate-600">
                  Nesta área você decide quais serviços o canal pode comercializar. Apenas serviços habilitados aqui
                  poderão ser vinculados a contratos das empresas daquele canal.
                </p>
              </div>
              <div className="flex flex-col gap-3 lg:min-w-[300px]">
                {canChooseAnyChannel ? (
                  <select
                    value={selectedChannelId ?? ""}
                    onChange={(event) => setSelectedChannelId(event.target.value ? Number(event.target.value) : null)}
                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Selecione um canal</option>
                    {(channelsData || []).map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.nomeFantasia || channel.razaoSocial || `Canal #${channel.id}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    Canal administrado: <strong className="text-slate-900">{currentChannelLabel}</strong>
                  </div>
                )}
                <Button onClick={handleSaveCatalog} disabled={setChannelCatalogMutation.isPending || !selectedChannelId} className="gap-2">
                  {setChannelCatalogMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  Salvar catálogo do canal
                </Button>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Canal selecionado</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{currentChannelLabel}</p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Serviços habilitados</p>
                <p className="mt-2 text-3xl font-semibold text-slate-900">{catalogSummary?.metrics?.servicesInCatalog ?? 0}</p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Serviços já contratados</p>
                <p className="mt-2 text-3xl font-semibold text-slate-900">{catalogSummary?.metrics?.servicesContracted ?? 0}</p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {(catalogSummary?.services || []).map((service) => {
                const checked = draftServiceIds.includes(service.id);
                return (
                  <label
                    key={service.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-3xl border p-5 transition ${checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                      checked={checked}
                      onChange={(event) => handleToggleCatalogService(service.id, event.target.checked)}
                      disabled={setChannelCatalogMutation.isPending}
                    />
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">{service.name}</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-500">{service.description || service.contractLabel}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                        <span className="rounded-full bg-white px-3 py-1">{service.category || "Sem categoria"}</span>
                        <span className="rounded-full bg-white px-3 py-1">{service.modules?.length || 0} módulo(s)</span>
                      </div>
                    </div>
                  </label>
                );
              })}

              {!isLoadingCatalog && (catalogSummary?.services || []).length === 0 && (
                <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                  Nenhum serviço ativo foi encontrado para a configuração atual do canal.
                </div>
              )}

              {isLoadingCatalog && (
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                  Carregando catálogo do canal selecionado...
                </div>
              )}
            </div>
          </section>
        )}

        <section className="grid gap-4 lg:grid-cols-2">
          {modules.map((module) => {
            const service = services.find((item) => item.moduleKey === module.moduleKey);
            const displayName = service?.serviceName || module.moduleKey.toUpperCase();

            return (
              <div key={module.moduleKey} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                    <Settings className="h-5 w-5" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <div className="flex items-center gap-2">
                        {module.userHasOperationalAccess ? (
                          <ShieldCheck className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <ShieldX className="h-4 w-4 text-amber-600" />
                        )}
                        <h2 className="text-lg font-semibold text-slate-900">{displayName}</h2>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {service?.contractLabel || "Serviço sem status contratual disponível no snapshot atual."}
                      </p>
                    </div>

                    <div className="grid gap-3">
                      <StatusRow label="Canal habilitado comercialmente" active={!!service?.channelHasService} />
                      <StatusRow label="Empresa apta para uso neste contexto" active={!!service?.companyHasService} />
                      <StatusRow label="Usuário com acesso operacional" active={module.userHasOperationalAccess} />
                    </div>

                    <div className="rounded-2xl bg-slate-950 p-4 text-sm leading-6 text-slate-300">
                      <p>
                        <strong className="text-white">Módulo técnico:</strong> {module.moduleKey.toUpperCase()}
                      </p>
                      <p className="mt-2">
                        <strong className="text-white">Escopo operacional:</strong> {module.scope}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {modules.length === 0 && (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-600 lg:col-span-2">
              Não há módulos registrados no snapshot atual do Portal PJC.
            </div>
          )}
        </section>
      </div>
    </SalviaLayout>
  );
}
